import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import ReactGA from 'react-ga4';
import SelectableBall from './SelectableBall';
import MyPage from './MyPage';
import FriendCode from './FriendCode';
import './Live.css';
import { useNavigate } from 'react-router-dom';
import { decode as base64Decode } from 'base64-arraybuffer';
import CryptoJS from 'crypto-js';
import {
    getUserDetails,
    getAudio,
    checkSession,
    logout,
} from './api';

const MicMonitor = () => {
    const [audioContext, setAudioContext] = useState(null);
    const [micStream, setMicStream] = useState(null);
    const sourceNodeRef = useRef(null); // ソースノードをrefで管理
    const gainNode = useRef(null);
    const convolverNodeRef = useRef(null); // ConvolverNodeをrefで管理
    const monoDownmixNodeRef = useRef(null); // ステレオ→モノラル変換用ノード
    const xfadeGainRef = useRef(null); // クロスフェード用ゲインノード
    const impulseResponsesRef = useRef(Array(9).fill(null)); // インパルス応答（再レンダリング不要なのでref）
    const [isStreaming, setIsStreaming] = useState(false);
    const [devices, setDevices] = useState([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState('');
    const [outputDevices, setOutputDevices] = useState([]); // 出力デバイスのリスト
    const [selectedOutputDeviceId, setSelectedOutputDeviceId] = useState(''); // 選択された出力デバイス
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const audioElementRef = useRef(null); // audio要素への参照を追加
    const destinationNodeRef = useRef(null); // MediaStreamDestinationNodeへの参照を追加

    const expirationRef = useRef('');
    const [referralCode, setReferralCode] = useState('');
    const [userData, setUserData] = useState(''); // ユーザー名のステートを追加
    const [showMyPage, setShowMyPage] = useState(false);
    const [showFriendCode, setShowFriendCode] = useState(false);
    const [selectedCircleIndex, setSelectedCircleIndex] = useState(4);
    const navigate = useNavigate();
    const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);

    useEffect(() => {
        const fetchUserDetails = async () => {
            try {
                const userData = await getUserDetails();
                if (userData) {
                    setUserData(userData);
                    expirationRef.current = userData.temporary_expiration;
                    if (userData.referral_code) {
                        setReferralCode(userData.referral_code); // Set referral_code
                    }
                }
            } catch (error) {
                console.error('Error fetching user details:', error);
            }
        };

        const handleIntervalActions = async () => {
            try {
                // セッション状態を確認
                const response = await checkSession();

                // セッションが無効な場合、ログインページにリダイレクト
                if (!response.data.logged_in) {
                    window.location.href = '/login';
                    return; // 他の処理を行わずに終了
                }

                // 他の処理を続行
                await fetchUserDetails();

            } catch (error) {
                console.error('Error in handleIntervalActions:', error);
                // 必要に応じてエラーメッセージを表示
            }
        };

        handleIntervalActions();
    }, []);

    useEffect(() => {
        if (userData) {
            // データレイヤーにユーザーIDとイベントをプッシュ
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'userLogin',
                user_id: userData.id
            });
        }
    }, [userData]);

    useEffect(() => {
        const loadImpulseResponses = async () => {
            try {
                const impulseFiles = [
                    'data_30_73.wav',
                    'data_30_48.wav',
                    'data_30_37.wav',
                    'data_30_23.wav',
                    'data_30_19.wav',
                    'data_30_15.wav',
                    'data_30_1.wav',
                    'data_30_62.wav',
                    'data_30_74.wav'
                ];

                const loadedResponses = await Promise.all(
                    impulseFiles.map(async file => {
                        return await loadAudio(file, audioContext);
                    })
                );

                impulseResponsesRef.current = loadedResponses;
            } catch (err) {
                console.error('インパルス応答の読み込みに失敗しました:', err);
            }
        };

        if (audioContext) {
            loadImpulseResponses();
        }
    }, [audioContext]);

    useEffect(() => {
        const requestMicrophoneAccess = async () => {
            try {
                await navigator.mediaDevices.getUserMedia({ audio: true });

                // オーディオデバイスを取得
                const deviceInfos = await navigator.mediaDevices.enumerateDevices();
                const audioInputDevices = deviceInfos.filter(device => device.kind === 'audioinput');
                const audioOutputDevices = deviceInfos.filter(device => device.kind === 'audiooutput');

                setDevices(audioInputDevices);
                setOutputDevices(audioOutputDevices);
            } catch (err) {
                if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                    console.error('マイクへのアクセスが拒否されました。');
                } else {
                    console.error('ストリームの取得に失敗しました:', err);
                }
            }
        };

        requestMicrophoneAccess();
    }, []);

    // Uint8ArrayをCryptoJS.lib.WordArrayに変換する関数
    const arrayBufferToWordArray = (ab) => {
        const i8a = new Uint8Array(ab);
        const a = [];
        for (let i = 0; i < i8a.length; i += 4) {
            a.push(
                (i8a[i] << 24) |
                (i8a[i + 1] << 16) |
                (i8a[i + 2] << 8) |
                (i8a[i + 3])
            );
        }
        return CryptoJS.lib.WordArray.create(a, i8a.length);
    };

    const loadAudio = async (fileName, context) => {
        try {
            const response = await getAudio(fileName);
            const encodedAudio = response.data.audio;
            const encodedKey = response.data.key;

            // Base64デコード
            const encryptedBytes = base64Decode(encodedAudio);
            const keyBytes = base64Decode(encodedKey);

            // AES復号化のためのキーとIVを設定
            const key = arrayBufferToWordArray(keyBytes);
            const iv = arrayBufferToWordArray(encryptedBytes.slice(0, 16));
            const encryptedData = arrayBufferToWordArray(encryptedBytes.slice(16));

            const decryptedData = CryptoJS.AES.decrypt(
                { ciphertext: encryptedData },
                key,
                { iv: iv, mode: CryptoJS.mode.CFB, padding: CryptoJS.pad.NoPadding }
            );

            // 復号化されたデータをUint8Arrayに変換
            const decryptedBytes = new Uint8Array(decryptedData.sigBytes);
            for (let i = 0; i < decryptedData.sigBytes; i++) {
                decryptedBytes[i] = (decryptedData.words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff;
            }

            return await context.decodeAudioData(decryptedBytes.buffer);
        } catch (error) {
            throw error;
        }
    };

    const handleDeviceChange = (event) => {
        const deviceId = event.target.value;
        setSelectedDeviceId(deviceId);

        if (!audioContext) {
            // latencyHintをinteractiveに設定して新しいAudioContextを作成
            const newAudioContext = new (window.AudioContext || window.webkitAudioContext)({
                latencyHint: 'interactive',
                sampleRate: 48000 // 一般的なオーディオデバイスのサンプルレート
            });
            setAudioContext(newAudioContext);
            // AudioContextのレイテンシー情報をコンソールに出力
            console.log(`AudioContext created. Base Latency: ${newAudioContext.baseLatency}s, Output Latency: ${newAudioContext.outputLatency}s`);
            if (newAudioContext.outputLatency > 0.04) {
                console.warn('警告: 出力遅延が大きいです。OS、オーディオドライバ、またはハードウェアが原因である可能性があります。可能であれば、オーディオを使用している他のアプリケーションを閉じてください。');
            }
        }
    };

    // ゲイン調整のスライドバーの変更時に呼び出される関数
    const handleGainChange = (event) => {
        ReactGA.event('click_gain', {
            event_category: 'Slider',
            event_label: 'Gain',
        });

        if (gainNode.current) {
            const newValue = parseFloat(event.target.value);
            gainNode.current.gain.value = newValue;  // useRefで参照しているgainNodeを更新
            console.log('Gain value set to:', newValue);
        }
    };

    const handleLogout = async () => {
        ReactGA.event('click_logout', {
            event_category: 'Button',
            event_label: 'Logout',
        });

        try {
            await logout();
            navigate('/login');
        } catch (error) {
            console.error('Error logging out', error);
        }
    };

    const startMonitoring = async () => {
        if (!audioContext) {
            console.error('AudioContextが作成されていません。');
            return;
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    deviceId: selectedDeviceId,
                    echoCancellation: false,
                    noiseSuppression: false,
                    autoGainControl: false,
                    latency: 0, // 可能な限り低遅延に設定
                    sampleRate: 48000 // AudioContextと同じサンプルレートを要求し、リサンプリングによる遅延を回避
                }
            });
            setMicStream(stream);

            const source = audioContext.createMediaStreamSource(stream);
            sourceNodeRef.current = source; // refに保存

            // 各ノードを生成してrefに保存
            gainNode.current = audioContext.createGain();
            gainNode.current.gain.value = 1;
            convolverNodeRef.current = audioContext.createConvolver();
            convolverNodeRef.current.normalize = false;

            // ステレオ→モノラル変換ノード
            // ch 0/ch 1 どちらに入力があっても自動でミックスされ、
            // バイノーラル処理（モノラル入力→ステレオ出力）が正しく機能する
            monoDownmixNodeRef.current = audioContext.createGain();
            monoDownmixNodeRef.current.channelCount = 1;
            monoDownmixNodeRef.current.channelCountMode = 'explicit';
            monoDownmixNodeRef.current.channelInterpretation = 'speakers';

            // 初期インパルス応答を設定
            convolverNodeRef.current.buffer = impulseResponsesRef.current[selectedCircleIndex];

            // クロスフェード用ゲインノード
            const xfadeGain = audioContext.createGain();
            xfadeGain.gain.value = 1;
            xfadeGainRef.current = xfadeGain;

            // デフォルトの接続（3Dボイス）: source -> monoDownmix -> convolver -> xfadeGain -> gain
            sourceNodeRef.current.connect(monoDownmixNodeRef.current);
            monoDownmixNodeRef.current.connect(convolverNodeRef.current);
            convolverNodeRef.current.connect(xfadeGain);
            xfadeGain.connect(gainNode.current);

            if (showAdvancedSettings && selectedOutputDeviceId) {
                // [低遅延に関する注意]
                // 以下の方法は、音声出力先デバイスを選択可能にしますが、<audio>要素を介するため、
                // audioContext.destinationへ直接接続する方法に比べて、追加のバッファリング遅延が発生します。
                // 遅延を最小化したい場合は、「高度な設定」をオフにしてください。
                const destinationNode = audioContext.createMediaStreamDestination();
                destinationNodeRef.current = destinationNode;
                gainNode.current.connect(destinationNode);

                if (audioElementRef.current) {
                    // audio要素の設定を最適化
                    audioElementRef.current.srcObject = destinationNode.stream;
                    audioElementRef.current.autoplay = true; // playの呼び出しを省略
                    audioElementRef.current.muted = false;
                    audioElementRef.current.volume = 1;
                    
                    // バッファリングを最小化
                    audioElementRef.current.preload = 'none';
                    audioElementRef.current.mozPreservesPitch = false; // Firefoxの場合
                    audioElementRef.current.preservesPitch = false;

                    try {
                        await audioElementRef.current.setSinkId(selectedOutputDeviceId);
                    } catch (err) {
                        console.error('出力デバイスの設定に失敗しました:', err);
                        // エラーが発生した場合は従来の実装にフォールバック
                        gainNode.current.disconnect(destinationNode);
                        gainNode.current.connect(audioContext.destination);
                    }
                }
            } else {
                // [低遅延のベストプラクティス]
                // こちらはWeb Audio APIの音声を直接デフォルトの出力デバイスに接続する、最も低遅延な方法です。
                gainNode.current.connect(audioContext.destination);
            }

            setIsStreaming(true);
        } catch (err) {
            console.error('マイクの取得に失敗しました:', err);
        } finally {
            setIsButtonDisabled(false);
        }
    };

    const stopMonitoring = () => {
        if (micStream) {
            micStream.getTracks().forEach(track => track.stop());
            
            // ノードの接続を解除
            if (sourceNodeRef.current) {
                sourceNodeRef.current.disconnect();
                sourceNodeRef.current = null;
            }
            if (monoDownmixNodeRef.current) {
                monoDownmixNodeRef.current.disconnect();
                monoDownmixNodeRef.current = null;
            }
            if (convolverNodeRef.current) {
                convolverNodeRef.current.disconnect();
                convolverNodeRef.current = null;
            }
            if (xfadeGainRef.current) {
                xfadeGainRef.current.disconnect();
                xfadeGainRef.current = null;
            }
            if (gainNode.current) {
                gainNode.current.disconnect();
                gainNode.current = null;
            }

            setMicStream(null);
            setIsStreaming(false);
            setIsButtonDisabled(false);

            // 高度な設定が有効な場合のみaudio要素をクリア
            if (showAdvancedSettings && audioElementRef.current) {
                audioElementRef.current.srcObject = null;
            }
        }
    };

    const toggleMonitoring = () => {
        ReactGA.event('click_startStop', {
            event_category: 'Button',
            event_label: 'Start_Stop',
        });

        setIsButtonDisabled(true);
        setIsPassThru(false);
        if (isStreaming) {
            stopMonitoring();
        } else {
            startMonitoring();
        }
    };

    const [isPassThru, setIsPassThru] = useState(false);

    const togglePassThru = () => {
        ReactGA.event('click_switch', {
            event_category: 'Button',
            event_label: 'Switch',
        });

        if (isStreaming && sourceNodeRef.current && monoDownmixNodeRef.current && convolverNodeRef.current && gainNode.current) {
            const nextIsPassThru = !isPassThru;
            setIsPassThru(nextIsPassThru);

            // すべての接続を一度解除
            sourceNodeRef.current.disconnect();
            convolverNodeRef.current.disconnect();
            xfadeGainRef.current?.disconnect();

            if (nextIsPassThru) {
                // パススルーをオンにする（モノラル）: source -> gain
                sourceNodeRef.current.connect(gainNode.current);
                console.log("Audio graph: source -> gain");
            } else {
                // パススルーをオフにする（3D）: source -> monoDownmix -> convolver -> xfadeGain -> gain
                sourceNodeRef.current.connect(monoDownmixNodeRef.current);
                monoDownmixNodeRef.current.connect(convolverNodeRef.current);
                convolverNodeRef.current.connect(xfadeGainRef.current);
                xfadeGainRef.current.connect(gainNode.current);
                console.log("Audio graph: source -> monoDownmix -> convolver -> xfadeGain -> gain");
            }
        }
    };

    const toggleMyPage = () => {
        ReactGA.event('click_settings', {
            event_category: 'Button',
            event_label: 'Settings',
        });

        setShowMyPage(!showMyPage);
    }

    const toggleFriendCode = () => {
        ReactGA.event('click_friendCode', {
            event_category: 'Button',
            event_label: 'Friend_Code',
        });

        setShowFriendCode(!showFriendCode);
    }

    const handleClickManual = () => {
        ReactGA.event('click_manual', {
            event_category: 'Button',
            event_label: 'Manual',
        });
    };

    const handleClickHelp = () => {
        ReactGA.event('click_help', {
            event_category: 'Button',
            event_label: 'Help',
        });
    };

    const handleClickContact = () => {
        ReactGA.event('click_contact', {
            event_category: 'Button',
            event_label: 'Contact',
        });
    };

    // インパルス応答の切り替え（クロスフェードでクリックノイズを防止）
    const switchImpulseResponse = (index) => {
        setSelectedCircleIndex(index);
        const ctx = audioContext;
        const newBuffer = impulseResponsesRef.current[index];
        if (!ctx || !monoDownmixNodeRef.current || !gainNode.current || !newBuffer) return;

        const FADE_TIME = 0.05;
        const now = ctx.currentTime;

        const newConvolver = ctx.createConvolver();
        newConvolver.normalize = false;
        newConvolver.buffer = newBuffer;

        const newXfadeGain = ctx.createGain();
        newXfadeGain.gain.setValueAtTime(0, now);
        newXfadeGain.gain.linearRampToValueAtTime(1, now + FADE_TIME);

        const oldConvolver = convolverNodeRef.current;
        const oldXfadeGain = xfadeGainRef.current;
        if (oldXfadeGain) {
            oldXfadeGain.gain.setValueAtTime(oldXfadeGain.gain.value, now);
            oldXfadeGain.gain.linearRampToValueAtTime(0, now + FADE_TIME);
        }

        monoDownmixNodeRef.current.connect(newConvolver);
        newConvolver.connect(newXfadeGain);
        newXfadeGain.connect(gainNode.current);

        setTimeout(() => {
            oldConvolver?.disconnect();
            oldXfadeGain?.disconnect();
        }, FADE_TIME * 1000 + 20);

        convolverNodeRef.current = newConvolver;
        xfadeGainRef.current = newXfadeGain;
    };

    // 出力デバイスの変更ハンドラを修正
    const handleOutputDeviceChange = async (event) => {
        const deviceId = event.target.value;
        setSelectedOutputDeviceId(deviceId);

        if (audioElementRef.current && deviceId) {
            try {
                await audioElementRef.current.setSinkId(deviceId);
            } catch (err) {
                console.error('出力デバイスの設定に失敗しました:', err);
            }
        }
    };

    return (
        <>
            <Helmet>
                <link
                    rel="stylesheet"
                    href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css"
                />
            </Helmet>
            {/* 非表示のaudio要素を追加 */}
            <audio ref={audioElementRef} style={{ display: 'none' }} />
            <div className="live-container">
                <aside className="sidebar">
                    <div className="user-info">
                        <i className="fas fa-user-circle user-icon"></i>
                        <span className="username">{userData ? userData.username : 'ユーザー名'}さん</span>
                    </div>
                    <div className="logo">
                        <img src="logo.svg" alt="imavo Logo" />
                    </div>
                    <hr className="logo-divider" />

                    <ul className="menu">
                        <li><a href="#" onClick={toggleFriendCode}><i class="fa-solid fa-user-group"></i> 友達紹介コード</a></li>
                        <li><a href="#" onClick={toggleMyPage}><i class="fa-solid fa-gear"></i> 設定</a></li>
                        <li><a href="https://imavo.notion.site/imavo-10402bc87cb7806a9994c463bdd8bafe?pvs=4" onClick={handleClickManual} target="_blank"><i class="fa-solid fa-book"></i> 使い方</a></li>
                        <li><a href="https://imavo.notion.site/ImaVo-LIVE-13102bc87cb780eab370d030fd71eedd?pvs=4" onClick={handleClickHelp} target="_black"><i className="fas fa-question-circle"></i> ヘルプ</a></li>
                        <li><a href="https://virtual-dummy-head.notion.site/13102bc87cb780d09ecbe60a6decfef7?pvs=105" onClick={handleClickContact} target="_black"><i className="fas fa-envelope"></i> お問い合わせ</a></li>
                        {/* <li><a href="#"><i className="fab fa-discord"></i> Discord</a></li> */}
                        <li><a href="#" onClick={handleLogout}><i className="fas fa-sign-out-alt"></i> ログアウト</a></li>
                    </ul>

                </aside>
                <div className="content">
                    <div className="live-column-container">
                        <div className="live-select-container">
                            <p className="label-live-mic">🎙️ マイク設定</p>
                            <select className="mic-select" onChange={handleDeviceChange} disabled={isStreaming} value={selectedDeviceId}>
                                <option value="">▼音声入力デバイスを選択してください</option>
                                {devices.map(device => (
                                    <option key={device.deviceId} value={device.deviceId}>
                                        {device.label || `Device ${device.deviceId}`}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="advanced-settings">
                            <label className="advanced-checkbox-label">
                                <span className="checkbox-text">
                                    🔊 出力設定
                                </span>
                                <input
                                    type="checkbox"
                                    style={{ marginLeft: '10px', marginRight: '-5px', transform: 'scale(0.9)' }} // 余白を追加し、サイズを小さくする
                                    checked={showAdvancedSettings}
                                    onChange={(e) => setShowAdvancedSettings(e.target.checked)}
                                    disabled={isStreaming}
                                />
                                <span style={{ fontSize: '0.9em' }}>高度な設定</span> {/* フォントサイズを小さくする */}
                            </label>

                            {showAdvancedSettings && (
                                <div className="output-device-select">
                                    <select
                                        className="mic-select"
                                        onChange={handleOutputDeviceChange}
                                        disabled={isStreaming}
                                        value={selectedOutputDeviceId}
                                    >
                                        <option value="">▼音声出力デバイスを選択してください</option>
                                        {outputDevices.map(device => (
                                            <option key={device.deviceId} value={device.deviceId}>
                                                {device.label || `Device ${device.deviceId}`}
                                            </option>
                                        ))}
                                    </select>
                                    <p className="output-device-warning">
                                        <i className="fas fa-exclamation-triangle"></i>
                                        環境によっては正常に動作しない場合があります
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="live-control-item">
                            <p className="label-live-gain">🎚️ ゲイン調整</p>
                            <input
                                type="range"
                                min="0"
                                max="2"
                                step="0.01"
                                defaultValue="1"
                                onChange={handleGainChange}
                                className="live-slider"
                            />
                        </div>
                        <div className="live-button-container">
                            <button
                                onClick={toggleMonitoring}
                                className={`live-button ${isStreaming ? "enable" : ""}`}
                                disabled={!selectedDeviceId || isButtonDisabled}
                            >
                                {isStreaming ? (
                                    <span>⏹ OFF</span>
                                ) : (
                                    <span>● 3Dボイス ON</span>
                                )}
                            </button>
                        </div>

                        <div className="live-button-container">
                            <button
                                onClick={togglePassThru}
                                className={`live-thru-button ${!isStreaming ? "" : isPassThru ? "blink-green" : "blink-red"}`}
                                disabled={!isStreaming}
                            >
                                {isPassThru ? (
                                    <>
                                        モノラルボイス再生中<br />
                                        <span className="small-text">（3D⇔モノラル切替ボタン）</span>
                                    </>
                                ) : (
                                    <>
                                        3Dボイス再生中<br />
                                        <span className="small-text">（3D⇔モノラル切替ボタン）</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                    <div className="live-pan-container">
                        <SelectableBall
                            switchImpulseResponse={switchImpulseResponse}
                            selectedCircleIndex={selectedCircleIndex}
                            setSelectedCircleIndex={setSelectedCircleIndex}
                        />
                        <div className="live-tips-container">
                            <img src="live_tips.svg" alt="Live Tips" className="live-tips-image" />
                        </div>
                    </div>
                    {showFriendCode && (
                        <div className="overlay" onClick={toggleFriendCode}>
                            <div className="overlay-content" onClick={(e) => e.stopPropagation()}>
                                <button className="close-icon-btn" onClick={toggleFriendCode}>
                                    <i className="fas fa-times"></i>
                                </button>
                                <FriendCode
                                    userData={userData}
                                    referralCode={referralCode}
                                />
                            </div>
                        </div>
                    )}
                    {showMyPage && (
                        <div className="overlay" onClick={toggleMyPage}>
                            <div className="overlay-content" onClick={(e) => e.stopPropagation()}>
                                <button className="close-icon-btn" onClick={toggleMyPage}>
                                    <i className="fas fa-times"></i>
                                </button>
                                <MyPage
                                    userData={userData}
                                />
                            </div>
                        </div>
                    )}
                </div>
                <div className="footer">
                    本サービスを利用したコンテンツ公開時は、#imavo または imavo のクレジット表記を必須としています。<br />
                    <a href="https://imavo.notion.site/15b02bc87cb780a08a0ec7a870d168ed?pvs=4" target="_blank">クレジット表記について詳細はこちら</a>
                </div>
            </div>
        </>
    );
}

export default MicMonitor;